import React, { useState, useRef } from 'react';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import DefaultLayout from '~/layout/components/DefaultLayout';
import ProductDetailPrice from '~/pdp/components/ProductDetailPrice';
import ReviewsPhotoDrawer from '~/pdp/components/ReviewsPhotoDrawer';
import { OverallFitPosition } from '~/pdp/constants/productDetail';
import ContentArea from '~/shared/components/ContentArea';
import ProductRating from '~/shared/components/ProductRating';
import ProductReviews from '~/shared/components/ProductReviews/ProductReviews';
import scrollToRef from '~/shared/utils/scrollToRef';
import { Link } from '~/techstyle-shared/next-routes';
import {
  mobile,
  ProductDetail,
  ProductName as BaseProductName,
  useProductImages,
  Picture,
  ProductReviewsContext,
  desktop,
  OverallFitSlider,
  Paginator,
  useTheme,
  useBreakpoint,
  ReviewsPhotoDrawerProvider,
  ReviewsDrawerProvider,
  useProductContext,
} from '~/techstyle-shared/react-components';
import { FormattedMessage, useIntl } from '~/techstyle-shared/react-intl';
import {
  useReviewsQuery,
  useProductQuery,
  useReviewsMetaDataQuery,
} from '~/techstyle-shared/react-products';

import createPage from '../../registry';

const ProductDetailWrapper = styled.div`
  flex: 1 1 auto;
  flex-direction: column;
  display: flex;
  margin-left: 32px;

  ${mobile`
    margin-left: 0;
    padding: 16px;
  `}
`;

const ProductName = styled(BaseProductName)`
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.sublabelBlack};
  letter-spacing: ${-0.6 / 24}em;
`;

const ProductContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;

  padding-right: 16px;
`;

const ShopNowButton = styled.button`
  border: none;
  padding: 6px;
  background-color: ${({ theme }) => theme.colors.fadedBackground};
  margin-top: 8px;
  width: 184px;
  cursor: pointer;
  border-radius: 4px;

  text-align: center;
  font-weight: bold;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textLight};
  font-family: ${({ theme }) => theme.fontStacks.baseFamily};

  ${mobile`
  width: 100%;
  `};
`;

const StyledPicture = styled(Picture)`
  && {
    width: 116px;
  }

  margin-bottom: 0;
`;

const ColorWrapper = styled.div`
  padding: 16px 0;

  ${desktop`
    padding: 32px 0;
    background-color: ${({ theme }) => theme.colors.reviewsColorWrapper};
  `};
`;

const ContentWrapper = styled(ContentArea)`
  display: flex;
  background-color: ${({ theme }) => theme.colors.palette.white};
  padding: 16px;

  & + & {
    margin-top: 16px;
  }

  ${({ theme }) => css`
    box-shadow: inset 0 -1px 0 0 ${theme.colors.reviewsContentWrapper},
      inset 0 1px 0 0 ${theme.colors.reviewsContentWrapper},
      inset 1px 0 0 0 ${theme.colors.reviewsContentWrapper},
      inset -1px 0 0 0 ${theme.colors.reviewsContentWrapper};
  `}

  ${mobile`
    flex-direction: column;
    box-shadow: none;
    margin: 0;
    padding: 0 16px;
  `};
`;

const RecommendWrapper = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100%;
  padding: 10px;

  border-radius: 4px;
  border: solid 1px ${({ theme }) => theme.colors.recommendedReviewBorder};

  font-family: ${({ theme }) => theme.fontStacks.body};

  ${mobile`
    margin-top: 24px;
    flex-direction: column;
    border: 0;
    padding: 24px;
    border-top: solid 1px ${({ theme }) =>
      theme.colors.recommendedReviewBorder};
  `}
`;

const RecommendPercent = styled.div`
  font-weight: bold;
  font-size: 18px;
  flex-direction: row;

  ${mobile`
    margin-bottom: 24px;
  `}
`;

const PercentValue = styled.p`
  color: ${({ theme }) => theme.colors.textEmphasized};
  display: inline;
`;

const overallFitStyle = css`
  text-align: center;
  font-weight: bold;
  width: 100%;

  ${mobile`
    display: none;
  `}
`;

const StyledSlider = styled(OverallFitSlider)`
  width: 100%;
`;

const blockStyle = css`
  box-shadow: none;

  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.recentReviewsBorder};
  }
`;

const PaginatorWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 16px;

  ${mobile`
    position: sticky;
    bottom: 0;
  `};
`;

const StyledPaginator = styled(Paginator)`
  font-family: ${({ theme }) => theme.fontStacks.body};
  padding: 16px 0;
`;

const RecommendComponent = ({ recommendedPercentage, sliderPosition }) => {
  const intl = useIntl();
  const overallFitAvailable = !!sliderPosition;

  return (
    <RecommendWrapper>
      <RecommendPercent>
        <PercentValue>
          {intl.formatNumber(recommendedPercentage / 100, {
            style: 'percent',
          })}
        </PercentValue>
        <FormattedMessage
          id="site_pdp.reviews_percent_who_recommend"
          defaultMessage="{percent} who purchased recommend it"
          values={{
            percent: '',
          }}
        />
      </RecommendPercent>
      {overallFitAvailable && (
        <StyledSlider
          minPosition={OverallFitPosition.MIN}
          maxPosition={OverallFitPosition.MAX}
          sliderPosition={sliderPosition}
          overallFitStyle={overallFitStyle}
        />
      )}
    </RecommendWrapper>
  );
};

RecommendComponent.propTypes = {
  recommendedPercentage: PropTypes.number,
  sliderPosition: PropTypes.number,
};

function ProductImage() {
  const product = useProductContext();
  const imageSets = useProductImages({
    product: product || {},
  });

  const secondaryImageBreakpoints = [{ minWidth: 0, pictureWidth: 116 }];
  const productImage = imageSets[0];

  if (!productImage) {
    return null;
  }

  return (
    <StyledPicture
      alt={product.label}
      breakpoints={secondaryImageBreakpoints}
      sources={productImage}
    />
  );
}

function ReviewsPageData({ groupCode }) {
  const { permalink } = useProductContext();
  const { mobile } = useBreakpoint();
  const PAGE_SIZE = mobile ? 5 : 10;
  const theme = useTheme();
  const [page, setPage] = useState(1);
  const productContainerRef = useRef(null);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    scrollToRef(productContainerRef);
  };

  const { data: reviewsMetaData, loading: reviewsMetaDataLoading } =
    useReviewsMetaDataQuery({
      groupCode,
    });

  const {
    data,
    previousData,
    loading: reviewsLoading,
  } = useReviewsQuery({
    groupCode,
    limit: PAGE_SIZE,
    offset: page === 1 ? 0 : (page - 1) * PAGE_SIZE,
  });

  if ((reviewsMetaDataLoading || reviewsLoading) && !previousData) {
    return <h2>Loading...</h2>;
  }

  const reviews = data?.reviewsData.reviews ?? [];

  const { overallFit, counts: { recommendedPercentage, reviewCount } = {} } =
    reviewsMetaData?.reviewsMetaData ?? {};

  return (
    <ReviewsDrawerProvider>
      <ProductReviewsContext reviews={reviews}>
        <ContentWrapper ref={productContainerRef}>
          <ProductContainer>
            <ProductImage />
            <ProductDetailWrapper>
              <ProductName />
              <ProductRating shouldPreventLinking />
              <ProductDetailPrice showEMPPrice={false} />
              <Link as={`/products/${permalink}`} href="/products/[identifier]">
                <a>
                  <ShopNowButton>
                    <FormattedMessage
                      id="site_pdp.reviews_seo_page_shop_now_cta"
                      defaultMessage="Shop Now"
                    />
                  </ShopNowButton>
                </a>
              </Link>
            </ProductDetailWrapper>
          </ProductContainer>
          <RecommendComponent
            sliderPosition={overallFit}
            recommendedPercentage={recommendedPercentage}
          />
        </ContentWrapper>

        <ReviewsPhotoDrawerProvider>
          <ContentWrapper>
            <ProductReviews
              isSeoPage
              recentReviewsStyle={{ blockStyle }}
              loading={reviewsLoading}
              pageSize={PAGE_SIZE}
            />
          </ContentWrapper>
          <ReviewsPhotoDrawer />
        </ReviewsPhotoDrawerProvider>
        <PaginatorWrapper>
          <StyledPaginator
            onPageChange={handlePageChange}
            totalCount={reviewCount}
            currentPage={page}
            pageSize={PAGE_SIZE}
            colors={{
              active: theme.colors.sublabelBlack,
              inactive: theme.colors.inactivePaginator,
              disabled: theme.colors.disabledLightBlack,
              arrow: theme.colors.sublabelBlack,
            }}
          />
        </PaginatorWrapper>
      </ProductReviewsContext>
    </ReviewsDrawerProvider>
  );
}

ReviewsPageData.propTypes = {
  groupCode: PropTypes.string,
};

function ReviewsPage({ masterProductId, permalink }) {
  const {
    data: productData,
    error: productDataError,
    loading: productDataLoading,
  } = useProductQuery({
    permalink,
    productId: masterProductId,
  });

  if (productDataError) {
    return <h2>There was an error loading the product.</h2>;
  }

  if (productDataLoading) {
    return <h2>Loading...</h2>;
  }

  const {
    product,
    product: { groupCode },
  } = productData;

  return (
    <DefaultLayout title={product.label}>
      <ColorWrapper>
        <ProductDetail product={product}>
          <ReviewsPageData groupCode={groupCode} />
        </ProductDetail>
      </ColorWrapper>
    </DefaultLayout>
  );
}

ReviewsPage.propTypes = {
  masterProductId: PropTypes.number,
  permalink: PropTypes.string,
};

ReviewsPage.getInitialProps = async (ctx) => {
  const { identifier } = ctx.query;

  let masterProductId;
  let permalink;
  if (/^\d+$/.test(identifier)) {
    masterProductId = parseInt(identifier, 10);
  } else {
    permalink = identifier;
  }

  return {
    permalink,
    masterProductId,
  };
};

export default createPage(ReviewsPage, {
  resourceBundles: [
    'site_product_page',
    'site_product_filter',
    'site_pdp',
    'site_reviews',
  ],
});
